import I18n from 'i18n-js';
import React, { useContext } from 'react';

import { RCAlertBox, RCCheckboxCard, RCIcon, RCRadioCardGroup, RCTag } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { ProtectionShieldIcon, CheckIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

import { SellFormContext, IShopConfig } from '../../SellFormContext';
import { Field, ActionType, IListingEntryState } from '../../sellFormReducerTypes';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';

const SAFE_SHIPPING_ARTICLE_ID = '360002670474';

const COUPLED_PROTECTION_TIER_MIN_USD_CENTS = 10000;
const COUPLED_PROTECTION_TIER_MAX_USD_CENTS = 150000;
const RSS_MAX_USD_CENTS = 1000000;

export enum FormVariant {
  RSS_OPTIONAL_ONLY = 'rssOptionalOnly',
  SIGNATURE_AND_RSS_BOTH_OPTIONAL = 'signatureAndRssBothOptional',
  COUPLED_PROTECTION = 'coupledProtection',
  ALWAYS_COVERED = 'alwaysCovered',
}

export function getFormVariant(listingEntry: IListingEntryState, shopConfig: IShopConfig): FormVariant {
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const shouldShowForm = packageEntryComplete(packageEntry) &&
                          shopConfig.currencySetting === 'USD' &&
                          !!currentPriceInput &&
                          currentPriceCents > 0 &&
                          currentPriceCents <= RSS_MAX_USD_CENTS;

  if (!shouldShowForm) { return null; }

  const isUps = !!packageEntry.allowedCarriers?.length && packageEntry.allowedCarriers[0] === 'UPS';

  if (currentPriceCents < COUPLED_PROTECTION_TIER_MIN_USD_CENTS) {
    // $0 - $99.99
    // -------------
    // UPS: seller can opt in/out of RSS (signature always included)
    // USPS: seller can opt in/out of RSS or signature, independent of each other
    return isUps ? FormVariant.RSS_OPTIONAL_ONLY : FormVariant.SIGNATURE_AND_RSS_BOTH_OPTIONAL;
  } else if (currentPriceCents <= COUPLED_PROTECTION_TIER_MAX_USD_CENTS) {
    // $100 - $1,500
    // -------------
    // UPS: seller can opt in/out of RSS (signature always included)
    // USPS: seller can choose from 3 radio options: [RSS+signature], signature only, or no protection
    return isUps ? FormVariant.RSS_OPTIONAL_ONLY : FormVariant.COUPLED_PROTECTION;
  } else if (currentPriceCents > COUPLED_PROTECTION_TIER_MAX_USD_CENTS) {
    // >$1,500.00
    // -------------
    // [RSS+signature] will always be required and included in carrier-calc price, no user input needed
    return FormVariant.ALWAYS_COVERED;
  }

  return null;
}

enum CoupledProtectionRadioOption {
  COUPLED_PROTECTION_YES = 'coupledProtectionYes',
  SIGNATURE_ONLY = 'signatureOnly',
  DECLINE_PROTECTION = 'declineProtection',
}

function currentRadioValue(packageEntry) {
  if (!packageEntry.forceSafeShipping && packageEntry.forceSignatureRequired) {
    return CoupledProtectionRadioOption.SIGNATURE_ONLY;
  } else if (packageEntry.forceSafeShipping) {
    return CoupledProtectionRadioOption.COUPLED_PROTECTION_YES;
  } else {
    return CoupledProtectionRadioOption.DECLINE_PROTECTION;
  }
}

export default function CarrierCalcAddOns() {
  const safeShippingHcUrl = useHelpCenterArticleUrl(SAFE_SHIPPING_ARTICLE_ID);
  const { listingEntry, shopConfig, dispatch } = useContext(SellFormContext);
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const formVariant = getFormVariant(listingEntry, shopConfig);

  if (!formVariant) { return null; }

  const showAlert = !packageEntry.forceSafeShipping && [
    FormVariant.RSS_OPTIONAL_ONLY,
    FormVariant.SIGNATURE_AND_RSS_BOTH_OPTIONAL,
    FormVariant.COUPLED_PROTECTION,
  ].includes(formVariant);

  return (
    <>
      <div className="d-flex mt-7 mb-5">
        <div className="mr-2" style={{ color: 'var(--rc-color-accent-loud)' }}>
          <RCIcon size="rc-space-12" svgComponent={ProtectionShieldIcon} />
        </div>
        <div>
          <h2 className="text-title-500 mb-1">
            {I18n.t(
              `discovery.sellForm.shippingSection.carrierCalcAddOns.${formVariant === FormVariant.ALWAYS_COVERED ? 'alwaysCoveredHeader' : 'header'}`)
            }
          </h2>
          <div className="mb-0 size-90">
            <span className="mr-1">
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.subheader')}
            </span>
            <CoreLink
              to={safeShippingHcUrl}
              target="_blank"
            >
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.learnMoreLinkText')}
            </CoreLink>
          </div>
        </div>
      </div>

      <>
        <div className="mb-4">
          {formVariant === FormVariant.RSS_OPTIONAL_ONLY && (
            <RCCheckboxCard
              checked={packageEntry.forceSafeShipping}
              label={(
                <>
                  <span className="mr-2">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssOptionLabel')}
                  </span>
                  <RCTag variant="highlight">
                    {I18n.t('commons.formGroup.recommended')}
                  </RCTag>
                </>
              )}
              sublabel={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssOptionSublabel')}
              onChange={() => {
                dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                  value: { ...packageEntry, ...{ forceSafeShipping: !packageEntry.forceSafeShipping } },
                });
              }}
            />
          )}
        </div>

        {formVariant === FormVariant.SIGNATURE_AND_RSS_BOTH_OPTIONAL && (
          <div className="d-grid grid-cols-1 gap-2 mb-4">
            <RCCheckboxCard
              checked={packageEntry.forceSafeShipping}
              label={(
                <>
                  <span className="mr-2">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssOptionLabel')}
                  </span>
                  <RCTag variant="highlight">
                    {I18n.t('commons.formGroup.recommended')}
                  </RCTag>
                </>
              )}
              sublabel={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssOptionSublabel')}
              onChange={() => {
                dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                  value: { ...packageEntry, ...{ forceSafeShipping: !packageEntry.forceSafeShipping } },
                });
              }}
            />
            <RCCheckboxCard
              checked={packageEntry.forceSignatureRequired}
              label={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.signatureCheckboxLabel')}
              sublabel={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.signatureSublabel')}
              onChange={() => {
                dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                  value: { ...packageEntry, ...{ forceSignatureRequired: !packageEntry.forceSignatureRequired } },
                });
              }}
            />
          </div>
        )}

        {formVariant === FormVariant.COUPLED_PROTECTION && (
          <RCRadioCardGroup
            id="rss-coupled-with-signature"
            name="rss-coupled-with-signature"
            onChange={(event) => {
              let updatedPackageEntry;

              if (event.target.value === CoupledProtectionRadioOption.COUPLED_PROTECTION_YES) {
                updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: true, forceSignatureRequired: true } };
              } else if (event.target.value === CoupledProtectionRadioOption.SIGNATURE_ONLY) {
                updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: false, forceSignatureRequired: true } };
              } else if (event.target.value === CoupledProtectionRadioOption.DECLINE_PROTECTION) {
                updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: false, forceSignatureRequired: false } };
              }

              dispatch({
                type: ActionType.UPDATE_FIELD,
                fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                value: updatedPackageEntry,
              });
            }}
            value={currentRadioValue(packageEntry)}
            label=""
          >
            <RCRadioCardGroup.Option
              value={CoupledProtectionRadioOption.COUPLED_PROTECTION_YES}
              label={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssAndSignatureCoupledRadioLabel')}
              sublabel={currentRadioValue(packageEntry) === CoupledProtectionRadioOption.COUPLED_PROTECTION_YES &&
                I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.rssOptionSublabel')
              }
              customTag={{ variant: 'highlight', label: I18n.t('commons.formGroup.recommended') }}
            />
            <RCRadioCardGroup.Option
              value={CoupledProtectionRadioOption.SIGNATURE_ONLY}
              label={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.signatureRequiredOnlyRadioLabel')}
              sublabel={currentRadioValue(packageEntry) === CoupledProtectionRadioOption.SIGNATURE_ONLY &&
                I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.signatureSublabel')
              }
            />
            <RCRadioCardGroup.Option
              value={CoupledProtectionRadioOption.DECLINE_PROTECTION}
              label={I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.declineProtectionRadioLabel')}
            />
          </RCRadioCardGroup>
        )}

        {formVariant === FormVariant.ALWAYS_COVERED && (
          <div
            className="d-flex padding-3"
            style={{
              border: '0.1rem solid var(--rc-color-palette-gray-400)',
              borderRadius: 'var(--rc-border-radius-md)',
            }}
          >
            <div className="ml-1 text-color-success">
              <RCIcon size="rc-space-6" svgComponent={CheckIcon} />
            </div>
            <div className="ml-2">
              <p className="mb-half size-90 text-utility--bold">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.alwaysCoveredCallout')}
              </p>
              <div className="mb-0 size-80">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.alwaysCoveredExplain')}
              </div>
            </div>
          </div>
        )}

        {showAlert && (
          <RCAlertBox
            type="warning"
            small
          >
            <span className="mr-1">
              <strong>
                {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.alertHighlight')}
              </strong>
            </span>
            <span>
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcAddOns.alertExplain')}
            </span>
          </RCAlertBox>
        )}
      </>
    </>
  );
}
